import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { formatCurrency } from "../lib/formatCurrency";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Plane, MapPin, MoreVertical, Edit, Trash2, Paperclip, Wallet, CalendarDays } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { RecordAttachments } from '../components/RecordAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVINCES = [
  ['gauteng', 'Gauteng'], ['limpopo', 'Limpopo'], ['mpumalanga', 'Mpumalanga'],
  ['north_west', 'North West'], ['free_state', 'Free State'], ['western_cape', 'Western Cape'],
  ['eastern_cape', 'Eastern Cape'], ['kwazulu_natal', 'KwaZulu-Natal'], ['northern_cape', 'Northern Cape'],
];
const provinceLabel = (k) => PROVINCES.find(([key]) => key === k)?.[1] || k;

const COST_FIELDS = [
  ['accommodation', 'Accommodation'], ['fuel', 'Fuel'], ['toll_fees', 'Toll Fees'], ['meals', 'Meals'],
  ['vehicle_service', 'Vehicle Service Costs'], ['parking', 'Parking'], ['other_expenses', 'Other Expenses'],
];

const EMPTY_TRIP = {
  traveler_id: 'none', start_date: '', end_date: '', province: 'gauteng', purpose: '',
  project_id: 'none', grant_id: 'none', cost_centre: '',
  accommodation: 0, fuel: 0, toll_fees: 0, meals: 0, vehicle_service: 0, parking: 0, other_expenses: 0,
  mileage_km: 0, mileage_rate: 0, notes: ''
};

export default function TravelPage() {
  const [trips, setTrips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provinceFilter, setProvinceFilter] = useState('all');

  const [showDialog, setShowDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [form, setForm] = useState(EMPTY_TRIP);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tripsRes, sumRes, usersRes, projectsRes, grantsRes] = await Promise.all([
        axios.get(`${API}/trips`),
        axios.get(`${API}/trips/summary`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/grants`).catch(() => ({ data: [] })),
      ]);
      setTrips(tripsRes.data);
      setSummary(sumRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setGrants(grantsRes.data);
    } catch (e) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const liveMileageCost = (Number(form.mileage_km) || 0) * (Number(form.mileage_rate) || 0);
  const liveTotal = COST_FIELDS.reduce((s, [k]) => s + (Number(form[k]) || 0), 0) + liveMileageCost;

  const buildPayload = () => ({
    ...form,
    traveler_id: form.traveler_id === 'none' ? null : form.traveler_id,
    project_id: form.project_id === 'none' ? null : form.project_id,
    grant_id: form.grant_id === 'none' ? null : form.grant_id,
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    purpose: form.purpose || null,
    cost_centre: form.cost_centre || null,
    notes: form.notes || null,
    ...Object.fromEntries(COST_FIELDS.map(([k]) => [k, Number(form[k]) || 0])),
    mileage_km: Number(form.mileage_km) || 0,
    mileage_rate: Number(form.mileage_rate) || 0,
  });

  const saveTrip = async () => {
    if (!form.province) { toast.error('Province is required'); return; }
    try {
      if (editingTrip) {
        await axios.put(`${API}/trips/${editingTrip.id}`, buildPayload());
        toast.success('Trip updated');
      } else {
        const res = await axios.post(`${API}/trips`, buildPayload());
        setEditingTrip(res.data);
        toast.success('Trip recorded — you can now attach receipts');
        loadData();
        return;
      }
      setShowDialog(false);
      setEditingTrip(null);
      setForm(EMPTY_TRIP);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save trip');
    }
  };

  const deleteTrip = async (id) => {
    if (!window.confirm('Delete this trip?')) return;
    try {
      await axios.delete(`${API}/trips/${id}`);
      toast.success('Trip deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete trip');
    }
  };

  const openEdit = (trip) => {
    setEditingTrip(trip);
    setForm({
      traveler_id: trip.traveler_id || 'none',
      start_date: trip.start_date || '', end_date: trip.end_date || '',
      province: trip.province, purpose: trip.purpose || '',
      project_id: trip.project_id || 'none', grant_id: trip.grant_id || 'none',
      cost_centre: trip.cost_centre || '',
      ...Object.fromEntries(COST_FIELDS.map(([k]) => [k, trip[k] || 0])),
      mileage_km: trip.mileage_km || 0, mileage_rate: trip.mileage_rate || 0,
      notes: trip.notes || ''
    });
    setShowDialog(true);
  };

  const getUserName = (id) => users.find(u => u.id === id)?.name || '-';
  const getProjectName = (id) => projects.find(p => p.id === id)?.name;
  const getGrantName = (id) => grants.find(g => g.id === id)?.name;

  const filteredTrips = provinceFilter === 'all' ? trips : trips.filter(t => t.province === provinceFilter);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading trips...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="travel-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Travel Management</h1>
          <p className="text-muted-foreground">Trips, expenses & receipts with auto-calculated costs</p>
        </div>
        <Button onClick={() => { setEditingTrip(null); setForm(EMPTY_TRIP); setShowDialog(true); }} data-testid="new-trip-btn">
          <Plus className="h-4 w-4 mr-2" /> New Trip
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Trips', value: summary?.total_trips, icon: Plane, cls: 'bg-black text-[#D4E100]', money: false, testid: 'travel-total-trips' },
          { label: 'Total Trip Cost', value: summary?.total_cost, icon: Wallet, cls: 'bg-blue-100 text-blue-600', money: true, testid: 'travel-total-cost' },
          { label: 'This Month', value: summary?.month_cost, icon: CalendarDays, cls: 'bg-emerald-100 text-emerald-600', money: true, testid: 'travel-month-cost' },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`h-12 w-12 rounded-lg ${k.cls} flex items-center justify-center shrink-0`}>
                <k.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-lg font-bold font-mono" data-testid={k.testid}>{k.money ? formatCurrency(k.value || 0) : (k.value || 0)}</p>
                <p className="text-sm text-muted-foreground">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-3 items-center">
        <Select value={provinceFilter} onValueChange={setProvinceFilter}>
          <SelectTrigger className="w-48" data-testid="travel-province-filter">
            <SelectValue placeholder="Province" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Provinces</SelectItem>
            {PROVINCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dates</TableHead>
                <TableHead>Traveler</TableHead>
                <TableHead>Province</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Linked To</TableHead>
                <TableHead>Cost Centre</TableHead>
                <TableHead className="text-right">Total Cost</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrips.map(trip => (
                <TableRow key={trip.id} data-testid={`trip-row-${trip.id}`}>
                  <TableCell className="text-sm font-mono">{trip.start_date || '-'}{trip.end_date ? ` → ${trip.end_date}` : ''}</TableCell>
                  <TableCell className="text-sm">{getUserName(trip.traveler_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="gap-1"><MapPin className="h-3 w-3" />{provinceLabel(trip.province)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{trip.purpose || '-'}</TableCell>
                  <TableCell className="text-xs">
                    {getGrantName(trip.grant_id) && <Badge className="bg-black text-[#D4E100] mr-1">{getGrantName(trip.grant_id)}</Badge>}
                    {getProjectName(trip.project_id) && <Badge variant="outline">{getProjectName(trip.project_id)}</Badge>}
                    {!trip.grant_id && !trip.project_id && '-'}
                  </TableCell>
                  <TableCell className="text-sm">{trip.cost_centre || '-'}</TableCell>
                  <TableCell className="text-right font-mono font-medium">{formatCurrency(trip.total_trip_cost)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`trip-menu-${trip.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(trip)} data-testid={`trip-edit-${trip.id}`}><Edit className="h-4 w-4 mr-2" /> Edit / Receipts</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => deleteTrip(trip.id)} data-testid={`trip-delete-${trip.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTrips.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No trips recorded yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Trip Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditingTrip(null); setForm(EMPTY_TRIP); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="trip-dialog">
          <DialogHeader>
            <DialogTitle>{editingTrip ? 'Edit Trip' : 'New Trip'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Traveler</Label>
                <Select value={form.traveler_id} onValueChange={(v) => setForm({ ...form, traveler_id: v })}>
                  <SelectTrigger data-testid="trip-traveler-select"><SelectValue placeholder="Traveler" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Province *</Label>
                <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v })}>
                  <SelectTrigger data-testid="trip-province-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROVINCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trip-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="trip-end-date" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Travel Purpose</Label>
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Reason for the trip" data-testid="trip-purpose-input" />
              </div>
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="trip-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Grant</Label>
                <Select value={form.grant_id} onValueChange={(v) => setForm({ ...form, grant_id: v })}>
                  <SelectTrigger data-testid="trip-grant-select"><SelectValue placeholder="Grant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Grant</SelectItem>
                    {grants.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Cost Centre</Label>
                <Input value={form.cost_centre} onChange={(e) => setForm({ ...form, cost_centre: e.target.value })} placeholder="e.g. CC-101 Training Delivery" data-testid="trip-cost-centre-input" />
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Trip Costs</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {COST_FIELDS.map(([k, l]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{l} (R)</Label>
                    <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`trip-${k}-input`} />
                  </div>
                ))}
                <div className="space-y-1">
                  <Label className="text-xs">Mileage (km)</Label>
                  <Input type="number" value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} data-testid="trip-mileage-km-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rate per km (R)</Label>
                  <Input type="number" step="0.01" value={form.mileage_rate} onChange={(e) => setForm({ ...form, mileage_rate: e.target.value })} data-testid="trip-mileage-rate-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mileage Cost</Label>
                  <div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 font-mono text-sm" data-testid="trip-mileage-cost">
                    {formatCurrency(liveMileageCost)}
                  </div>
                </div>
              </div>
              <div className="mt-3 rounded-lg border-2 border-black p-3 flex items-center justify-between">
                <span className="font-semibold">Total Trip Cost (auto-calculated)</span>
                <span className="font-bold font-mono text-lg" data-testid="trip-total-cost">{formatCurrency(liveTotal)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Additional notes" data-testid="trip-notes-input" />
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1 mb-1"><Paperclip className="h-3 w-3" /> Receipts</Label>
              {editingTrip?.id ? (
                <RecordAttachments parentType="trip" parentId={editingTrip.id} compact />
              ) : (
                <p className="text-xs text-muted-foreground italic">Save the trip first, then upload receipts here.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="trip-cancel-btn">Close</Button>
            <Button onClick={saveTrip} data-testid="trip-save-btn">{editingTrip ? 'Update Trip' : 'Save Trip'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
